import { Component, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ButtonComponent, DialogComponent, TextDirective } from '@joii/design-system';
import { LucideDynamicIcon, LucidePhoneIncoming } from '@lucide/angular';
import { PushNotificationService } from '../../services/push-notification.service';

export interface PushNotificationDialogData {
  title: string;
  body: string;
  url?: string;
}

@Component({
  selector: 'app-push-notification-dialog',
  standalone: true,
  imports: [DialogComponent, ButtonComponent, LucideDynamicIcon, TextDirective],
  template: `
    <joii-dialog #dialogRef [variant]="'flexible-sized-modal'">
      <div class="push-notification-card">
        <div class="push-notification-content">
          <div class="push-notification-header">
            <div class="push-notification-icon">
              <svg [lucideIcon]="phoneIncomingIcon" [size]="24" />
            </div>

            <p joii-text class="push-notification-title" joii-text [size]="6" [weight]="'semibold'">
              {{ data?.title ?? 'Your clinician has joined the video call' }}
            </p>
          </div>
          <p class="push-notification-body" joii-text [size]="5" color="base">
            {{ data?.body ?? 'Tap to join the video session' }}
          </p>
        </div>
        <button joii-button class="push-notification-join-btn" variant="primary" (click)="onJoinClick()">
          Join video call
        </button>
      </div>
    </joii-dialog>
  `,
  styles: `
    .push-notification-card {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 8px 0;
      max-width: 361px;
      margin: 8px;
    }

    .push-notification-content {
      background: var(--Vetai-Backgrounds--Tint_1, #f4f2f0);
      border-radius: 10px;
      padding: 8px 8px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .push-notification-header {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .push-notification-icon {
      width: 40px;
      height: 40px;
      min-width: 40px;
      border-radius: 100px;
      background: rgba(83, 198, 145, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--Vetai-Status_packages--Success--Default, #53c691);
    }

    .push-notification-title {
      flex: 1;
      margin: 0;
    }

    .push-notification-body {
      margin: 0;
    }

    .push-notification-join-btn {
      width: 100%;
      border-radius: 100px;
    }
  `,
})
export class PushNotificationDialogComponent implements OnInit {
  private router = inject(Router);
  private pushNotificationService = inject(PushNotificationService);
  private destroyRef = inject(DestroyRef);

  dialogRef = viewChild.required<DialogComponent>('dialogRef');
  phoneIncomingIcon = LucidePhoneIncoming;

  data: PushNotificationDialogData | null = null;

  ngOnInit(): void {
    this.pushNotificationService.dialogData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((d) => {
      this.data = d;
      this.dialogRef().open();
    });
  }

  async onJoinClick(): Promise<void> {
    await this.dialogRef().close({ immediate: true });
    if (this.data?.url) {
      this.router.navigateByUrl(this.data.url);
    }
  }

  onClose(): void {
    this.dialogRef().close();
  }
}
